import React, { FunctionComponent, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Picture, createPictureFactory } from '@funtech-int/yango-shared';

import { useOpenedChannelIdQueryParam } from '../../hooks/useOpenedChannelIdQueryParam';
import { useChannelWallpapers } from '../../hooks/useChannelWallpapers';
import { ChannelWallpaperManager } from '../../lib/channelWallpaperManager';
import { getPaywallChannels } from '../../lib/utils/getPaywallChannels';
import { ChannelWallpaperItem } from '../../lib/utils/getChannelsWallpapers';

import styles from './ChannelCardWallpaper.module.css';

const lPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '260x260',
            '2x': '555x555',
        },
        src: '260x260',
    },
});

const mPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '210x210',
            '2x': '420x420',
        },
        src: '210x210',
    },
});

type ItemSize = 'm' | 'l';

interface WallpaperItemProps {
    img?: string;
    title?: string;
    size?: ItemSize;
    className?: string;
}

const ChannelCardWallpaperItemComponent: FunctionComponent<WallpaperItemProps> = ({
    img,
    title,
    className,
    size = 'm',
}) => {
    const [isLoadingImageFailed, setIsLoadingImageFailed] = useState(false);

    const fallback = useMemo(() => {
        const factory = size === 'l' ? lPictureFactory : mPictureFactory;

        return factory({ fallbackUrl: img }).img;
    }, [img, size]);

    return img && !isLoadingImageFailed ? (
        <Picture
            className={classnames(className, styles.image, styles[size])}
            alt={title || ''}
            loading="lazy"
            srcSet={fallback.srcSet}
            src={fallback.src}
            onError={() => setIsLoadingImageFailed(true)}
            data-tid="AdaptiveImage"
        />
    ) : (
        <div className={classnames(styles.image, styles[size], className)} />
    );
};

interface ChannelCardWallpaperProps {
    className?: string;
    currentChannelWallpaper: ChannelWallpaperItem;
    wallpapperChannelsManager: ChannelWallpaperManager;
}

export const ChannelCardWallpaper = ({
    className,
    currentChannelWallpaper,
    wallpapperChannelsManager,
}: ChannelCardWallpaperProps) => {
    const allowedChannelWallpapers = useChannelWallpapers({ wallpapperChannelsManager });
    const channelId = useOpenedChannelIdQueryParam();

    const channelsWallpapers = getPaywallChannels({
        allowedChannelWallpapers,
        currentChannelWallpaper,
    });

    if (!channelId) {
        return null;
    }

    if (!channelsWallpapers) {
        return null;
    }

    return (
        <div className={classnames(styles.root, className)}>
            <div className={styles.overlay} />
            <div className={styles.line}>
                {channelsWallpapers.slice(0, 4).map((channel, index) => (
                    <ChannelCardWallpaperItemComponent
                        key={channel?.contentId || index}
                        img={channel?.logo ?? undefined}
                        title={channel?.title}
                        className={styles.item}
                    />
                ))}
            </div>
            <div className={classnames(styles.line, styles['middle-line'])}>
                {channelsWallpapers.slice(4, 9).map((channel, index) => (
                    <ChannelCardWallpaperItemComponent
                        key={channel?.contentId || index}
                        img={channel?.logo ?? undefined}
                        title={channel?.title}
                        className={styles.item}
                        size={index === 2 ? 'l' : 'm'}
                    />
                ))}
            </div>
            <div className={styles.line}>
                {channelsWallpapers.slice(9, 14).map((channel, index) => (
                    <ChannelCardWallpaperItemComponent
                        key={channel?.contentId || index}
                        img={channel?.logo ?? undefined}
                        title={channel?.title}
                        className={styles.item}
                    />
                ))}
            </div>
        </div>
    );
};
